// src/components/CompareProfiles.js
import React, { useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './CompareProfiles.css'; // Import the CSS for styling
import { supabase } from '../supabaseClient'; // Adjust path if needed
import { checkUserComparisonLimit, trackUserComparison, getUserComparisonStatus } from '../utils/userComparisonTracking';
// Optional: If you use an icon library like react-icons
// import { FiArrowRightCircle, FiMinusSquare } from 'react-icons/fi'; // Example icons

export default function CompareProfiles() {
  // --- State Variables ---
  const [pair, setPair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [voting, setVoting] = useState(false);
  const [showRatings, setShowRatings] = useState(false);
  const [votedProfileId, setVotedProfileId] = useState(null);
  const [isTieVote, setIsTieVote] = useState(false);

  // --- User Comparison Tracking State ---
  const [currentUser, setCurrentUser] = useState(null);
  const [comparisonStatus, setComparisonStatus] = useState('');
  const [comparisonLimitReached, setComparisonLimitReached] = useState(false);
  const [canMakeComparisons, setCanMakeComparisons] = useState(true);

  // --- Load Pair Function ---
  const loadPair = useCallback(async () => {
    setLoading(true);
    setError(null);
    setShowRatings(false);
    setVotedProfileId(null);
    setIsTieVote(false); // <<< Reset tie state
    setPair(null);

    try {
      // ... (Supabase get-comparison-pair call remains the same) ...
       console.log("React: Invoking get-comparison-pair function...");
       const { data, error: functionError } = await supabase.functions.invoke(
         'get-comparison-pair'
       );
       console.log("React: Function response (get-comparison-pair):", { data, functionError });
       if (functionError) throw functionError; // Let catch block handle details
       if (!data || !data.a || !data.b || !data.a.id || !data.b.id) {
           throw new Error("Received invalid data structure from the server function.");
       }
       setPair(data);
    } catch (e) {
       console.error("React: Failed to load profiles:", e);
       // Process error message (keep your existing detailed logic here)
       let specificMessage = e.message || 'Unknown error';
       if (e.context && typeof e.context.json === 'function') { try { const json = await e.context.json(); specificMessage = json.error || specificMessage; } catch { /* ignore */ } }
       else if (e instanceof Error && e.message.includes('non-2xx status code')) { specificMessage = `Server function failed: Edge Function returned status ${e.context?.status || 'unknown'}.`; }
       else if (e.message.includes('Not enough profiles')) { specificMessage = 'Not enough profiles available to compare right now.'; }
       setError(`Failed to load profiles: ${specificMessage}.`);
       setPair(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // --- Handle Vote Function (Win/Loss) ---
  const handleVote = async (winnerId, loserId) => {
    if (voting || !pair || showRatings) return;
    
    // Check comparison limit before allowing vote
    if (!canMakeComparisons) {
      setError('Weekly comparison limit reached (3/3 used). Resets Monday at midnight.');
      return;
    }
    
    setVoting(true);
    setIsTieVote(false); // <<< Ensure tie state is false
    setVotedProfileId(winnerId); // Store winner ID
    setError(null);

    // ... (Supabase record-vote call and rating update remain the same) ...
    console.log(`React: Invoking record-vote function: Winner=${winnerId}, Loser=${loserId}`);
    if (!winnerId || !loserId) { /* ... error handling ... */ setVotedProfileId(null); setVoting(false); return; }
    try {
      const { data, error: functionError } = await supabase.functions.invoke('record-vote', {
        body: { winnerId, loserId }
      });
       if (functionError) throw functionError; // Let catch block handle
      console.log("React: Vote successful, showing ratings...");
      
      // Track the comparison (increment count)
      if (currentUser?.id) {
        console.log('Tracking comparison for user:', currentUser.id);
        const trackResult = await trackUserComparison(currentUser.id, `comparison_${winnerId}_${loserId}`);
        console.log('Track result:', trackResult);
        if (trackResult) {
          // Update comparison status after tracking
          const newStatus = await getUserComparisonStatus(currentUser.id);
          console.log('New status:', newStatus);
          setComparisonStatus(newStatus);
          
          const limitCheck = await checkUserComparisonLimit(currentUser.id);
          console.log('Limit check:', limitCheck);
          setComparisonLimitReached(!limitCheck.canCompare);
          setCanMakeComparisons(limitCheck.canCompare);
        }
      } else {
        console.warn('No current user found for tracking comparison');
      }
      
       if (data && data.success && pair) { // Update pair state with new ratings
           setPair(currentPair => {
               if (!currentPair) return null;
               // Store the current ratings before updating
               const aOldRating = currentPair.a.rating;
               const bOldRating = currentPair.b.rating;
               
               const newA = currentPair.a.id === winnerId 
                 ? { 
                     ...currentPair.a, 
                     rating: data.winnerNewRating,
                     oldRating: aOldRating  // Use stored old rating
                   } 
                 : currentPair.a.id === loserId 
                   ? { 
                       ...currentPair.a, 
                       rating: data.loserNewRating,
                       oldRating: aOldRating  // Use stored old rating
                     } 
                   : currentPair.a;
               
               const newB = currentPair.b.id === winnerId 
                 ? { 
                     ...currentPair.b, 
                     rating: data.winnerNewRating,
                     oldRating: bOldRating  // Use stored old rating
                   } 
                 : currentPair.b.id === loserId 
                   ? { 
                       ...currentPair.b, 
                       rating: data.loserNewRating,
                       oldRating: bOldRating  // Use stored old rating
                     } 
                   : currentPair.b;
               
               return { a: newA, b: newB };
           });
       }
      setShowRatings(true);

    } catch (e) {
       console.error("React: Failed to submit vote:", e);
       // Process error message (keep your existing detailed logic here)
       let specificMessage = e.message || 'Unknown error';
       if (e.context && typeof e.context.json === 'function') { try { const json = await e.context.json(); specificMessage = json.error || specificMessage; } catch { /* ignore */ } }
       else if (e instanceof Error && e.message.includes('non-2xx status code')) { specificMessage = `Server function failed: Edge Function returned status ${e.context?.status || 'unknown'}.`; }
      setError(`Failed to submit vote: ${specificMessage}. Please try again.`);
       setShowRatings(false);
       setVotedProfileId(null); // Reset vote state on error
    } finally {
      setVoting(false);
    }
  };

  // --- Handle Tie Function ---
  const handleTie = async (profileAId, profileBId) => {
    if (voting || !pair || showRatings) return;
    
    // Check comparison limit before allowing tie vote
    if (!canMakeComparisons) {
      setError('Weekly comparison limit reached (3/3 used). Resets Monday at midnight.');
      return;
    }
    
    setVoting(true);
    setIsTieVote(true);
    setVotedProfileId(null);
    setError(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('record-vote', {
        body: { 
          winnerId: profileAId, // Using same parameter names as regular vote
          loserId: profileBId,
          isTie: true // Add flag to indicate this is a tie
        }
      });

      if (functionError) throw functionError;

      // Track the comparison (increment count) for tie
      if (currentUser?.id) {
        console.log('Tracking tie comparison for user:', currentUser.id);
        const trackResult = await trackUserComparison(currentUser.id, `tie_${profileAId}_${profileBId}`);
        console.log('Tie track result:', trackResult);
        if (trackResult) {
          // Update comparison status after tracking
          const newStatus = await getUserComparisonStatus(currentUser.id);
          console.log('New tie status:', newStatus);
          setComparisonStatus(newStatus);
          
          const limitCheck = await checkUserComparisonLimit(currentUser.id);
          console.log('Tie limit check:', limitCheck);
          setComparisonLimitReached(!limitCheck.canCompare);
          setCanMakeComparisons(limitCheck.canCompare);
        }
      } else {
        console.warn('No current user found for tracking tie comparison');
      }

      if (data && data.success && pair) {
        setPair(currentPair => {
          if (!currentPair) return null;
          // Store the current ratings before updating
          const aOldRating = currentPair.a.rating;
          const bOldRating = currentPair.b.rating;
          
          return {
            a: { 
              ...currentPair.a, 
              rating: data.winnerNewRating,
              oldRating: aOldRating  // Use stored old rating
            },
            b: { 
              ...currentPair.b, 
              rating: data.loserNewRating,
              oldRating: bOldRating  // Use stored old rating
            }
          };
        });
      }
      setShowRatings(true);

    } catch (e) {
      console.error("Failed to submit tie:", e);
      let specificMessage = e.message || 'Unknown error';
      if (e.context && typeof e.context.json === 'function') {
        try {
          const json = await e.context.json();
          specificMessage = json.error || specificMessage;
        } catch { /* ignore */ }
      }
      setError(`Failed to submit tie: ${specificMessage}. Please try again.`);
      setShowRatings(false);
      setIsTieVote(false);
    } finally {
      setVoting(false);
    }
  };

  // --- Initialize User Tracking ---
  useEffect(() => {
    const initializeUserTracking = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        console.log('Current user for tracking:', user);
        setCurrentUser(user);
        
        if (user?.id) {
          console.log('Initializing tracking for user ID:', user.id);
          const status = await getUserComparisonStatus(user.id);
          console.log('Initial status:', status);
          setComparisonStatus(status);
          
          const limitCheck = await checkUserComparisonLimit(user.id);
          console.log('Initial limit check:', limitCheck);
          setComparisonLimitReached(!limitCheck.canCompare);
          setCanMakeComparisons(limitCheck.canCompare);
        } else {
          console.warn('No user found during initialization');
        }
      } catch (error) {
        console.error('Error initializing user tracking:', error);
        // Default to allowing comparisons if user tracking fails
        setCanMakeComparisons(true);
      }
    };

    initializeUserTracking();
  }, []);

  // --- Initial Load Effect ---
  useEffect(() => {
    loadPair();
  }, [loadPair]);

  // --- Render Helper for a Single Profile Card ---
  const renderProfileCard = (profile, voteHandler) => {
    if (!profile) return <div className="profile-card error-card">Profile data missing.</div>;

    // displayValue, renderStatsLine, renderAPStatsLine remain the same
    const displayValue = (value, naValue = 'N/A') => { /* ... */ if (value === null || value === undefined) return naValue; if (typeof value === 'string' && value.trim() === '') return naValue; if (Array.isArray(value)) { const filtered = value.filter(item => typeof item === 'string' && item.trim()); if (filtered.length > 0 && (filtered.length > 1 || filtered[0].length > 20)) { return (<ul className="data-list">{filtered.map((item, index) => <li key={index}>{item}</li>)}</ul>); } return filtered.length > 0 ? filtered.join('; ') : naValue; } if (profile && value === profile.gpa && typeof value === 'number') { return value.toFixed(2); } if (profile && value === profile.rating && typeof value === 'number') { return Math.round(value); } return value; };
    const renderStatsLine = (p) => { /* ... */ const parts = [p.gpa ? `GPA: ${p.gpa.toFixed(2)}` : null, p.sat_score ? `SAT: ${p.sat_score}` : null, p.act_score ? `ACT: ${p.act_score}` : null,].filter(Boolean); return parts.length > 0 ? parts.join(' | ') : 'N/A'; };
    const renderAPStatsLine = (p) => { /* ... */ const parts = [p.ap_num_taken ? `# APs Taken: ${p.ap_num_taken}` : null, p.ap_num_fives ? `# AP 5s: ${p.ap_num_fives}` : null,].filter(Boolean); return parts.length > 0 ? parts.join(' | ') : 'N/A'; };


    const isVotedFor = !isTieVote && votedProfileId === profile.id; // Check if THIS was the WINNER
    const isVoteFinished = showRatings; // Vote is finished if ratings are shown

    // --- Profile Card JSX Structure ---
    // <<< MODIFIED: Add 'voted-tie' class dynamically >>>
    return (
      <article className={`profile-card fade-in-card ${isVoteFinished && isTieVote ? 'voted-tie' : ''} ${isVotedFor ? 'voted-for' : ''} ${isVoteFinished && !isVotedFor && !isTieVote ? 'voted-against' : ''}`} key={profile.id}>
        <header className="profile-card-header">
             <h3>Profile</h3>
             <span className="class-year">(Class of {displayValue(profile.class_year)})</span>
        </header>

        {/* Content sections remain the same */}
        <div className="profile-card-content">
            {/* ... sections ... */}
             <section className="profile-section essay-section">
                <label>Essay:</label>
                <div className="markdown-content">
                    {comparisonLimitReached ? (
                        <div className="essay-restricted">
                            <div className="essay-restriction-message">
                                <p>📝 Essay content hidden</p>
                                <small>You've reached your weekly comparison limit (3/3 used)</small>
                            </div>
                        </div>
                    ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {profile.essay || '*Essay not provided.*'}
                        </ReactMarkdown>
                    )}
                </div>
            </section>
            <section className="profile-section">
                <label>Top ECs:</label>
                <div className="data-value">{displayValue(profile.extracurriculars)}</div>
            </section>
             <section className="profile-section">
                <label>Academic Stats:</label>
                <p className="data-value">{renderStatsLine(profile)}</p>
            </section>
            <section className="profile-section">
                <label>AP Performance:</label>
                <p className="data-value">{renderAPStatsLine(profile)}</p>
            </section>
            <section className="profile-section">
                <label>Awards/Honors:</label>
                <div className="data-value">{displayValue(profile.awards)}</div>
            </section>
            {profile.special_program && (
                <section className="profile-section">
                    <label>Intended Program/Major:</label>
                    <p className="data-value">{displayValue(profile.special_program)}</p>
                </section>
            )}
        </div>

        <footer className="profile-card-footer">
             {/* Rating display logic remains the same */}
             {showRatings && (
                <div className="rating-display fade-in-rating">
                    <label>Rating:</label>
                    <div className="rating-value-container">
                        <span className="rating-value">{displayValue(profile.rating)}</span>
                        {showRatings && (
                          <span className={`rating-change ${
                            profile.id === votedProfileId ? 'positive' : 
                            isTieVote ? 'tie' : 'negative'
                          }`}>
                            {/* Calculate and display the rating change */}
                            {profile.rating > profile.oldRating ? '+' : ''}
                            {profile.rating - profile.oldRating}
                          </span>
                        )}
                    </div>
                </div>
             )}
             {!showRatings && <div className="rating-placeholder" style={{ height: '2.5em', marginBottom: '10px' }}></div>}

            {/* --- VOTE BUTTON (Updated Logic for Tie) --- */}
            <button
                onClick={voteHandler}
                // Disable if loading, voting, vote is finished, or comparison limit reached
                disabled={loading || voting || isVoteFinished || comparisonLimitReached}
                className={`button button-vote ${comparisonLimitReached ? 'disabled' : ''}`}
            >
                {/* Adjust text based on win/loss/tie state */}
                {voting && isVotedFor ? 'Processing...' : // Voting for THIS card
                 voting && isTieVote ? '' : // Processing a TIE (hide text on vote button)
                 isVoteFinished && isVotedFor ? 'Voted ✔' :  // This card WON
                 isVoteFinished && isTieVote ? 'Tied =' :     // It was a TIE
                 isVoteFinished ? '' :                     // This card LOST (hide text)
                 'Vote for this Profile'                   // Initial state
                }
            </button>
            {/* --- END VOTE BUTTON --- */}
        </footer>
      </article>
    );
  };

  // --- Conditional Rendering Logic (Loading/Error/Empty) ---
  // Remains the same
  if (loading && !pair) { /* ... */ return <div className="compare-message loading">Loading profiles... <span className="spinner"></span></div>; }
  if (error) { /* ... */ return ( <div className="compare-profiles-container"><div className="compare-message error"><p><strong>Error:</strong> {error}</p><button onClick={loadPair} disabled={loading} className="button retry-button">{loading ? 'Loading...' : 'Try Again'}</button></div></div> ); }
  if (!loading && !pair && !error) { /* ... */ return ( <div className="compare-profiles-container"><div className="compare-message">No profiles available for comparison right now. Check back later!</div><button onClick={loadPair} disabled={loading} className="button retry-button">{loading ? 'Loading...' : 'Check Again'}</button></div> ); }

  // --- Main Comparison View ---
  return (
    <div className="compare-profiles-container">
      <h2 className="page-title which-stronger-title">Which Profile is Stronger?</h2>
      
      {/* Comparison Status Banner */}
      {comparisonStatus && (
        <div className={`comparison-status-banner ${comparisonLimitReached ? 'warning' : ''}`} style={{
          background: comparisonLimitReached ? 
            'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)' :
            'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          border: `1px solid ${comparisonLimitReached ? '#ffeaa7' : '#dee2e6'}`,
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          marginBottom: '1.5rem',
          textAlign: 'center'
        }}>
          <small style={{
            color: comparisonLimitReached ? '#856404' : '#6c757d',
            fontWeight: '500',
            fontSize: '0.875rem'
          }}>
            {comparisonLimitReached ? '⚠️ ' : ''}Weekly Comparison Status: {comparisonStatus}
          </small>
        </div>
      )}
      
      {/* Show limit reached message if applicable */}
      {comparisonLimitReached && (
        <div className="comparison-limit-warning" style={{
          padding: '1rem',
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '8px',
          color: '#856404',
          marginBottom: '1.5rem',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>Weekly Comparison Limit Reached</h3>
          <p style={{ margin: '0.5rem 0' }}>
            You've used all 3 of your weekly comparisons. New comparisons will be available Monday at midnight.
          </p>
          <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }}>
            You can still view individual profiles through the leaderboard and unlocked profiles pages.
          </p>
        </div>
      )}

      {/* Loading overlay */}
      {(loading && pair) && (
        <div className="loading-overlay"><div><span className="spinner"></span> Loading next pair...</div></div>
      )}

      {/* Comparison area */}
      <div className={`profile-comparison-area ${loading ? 'area-loading' : ''} ${comparisonLimitReached ? 'limit-reached' : ''}`}>
        {pair?.a && renderProfileCard(pair.a, () => handleVote(pair.a.id, pair.b.id))}
        <div className="vs-separator" aria-hidden="true">VS</div>
        {pair?.b && renderProfileCard(pair.b, () => handleVote(pair.b.id, pair.a.id))}
      </div>

      {/* Center the action buttons below both profiles */}
      <div className="central-action-area">
        <div className={`action-button-container ${showRatings ? 'next-visible' : 'tie-visible'}`}>
          {/* Show TIE button BEFORE vote */}
          {!showRatings && pair && (
            <button
              onClick={() => handleTie(pair.a.id, pair.b.id)}
              disabled={loading || voting || comparisonLimitReached}
              className={`button button-tie ${comparisonLimitReached ? 'disabled' : ''}`}
            >
              {voting && isTieVote ? 'Processing...' : 
               comparisonLimitReached ? 'Limit Reached' : 'Declare a Tie'}
            </button>
          )}

          {/* Show NEXT button AFTER vote */}
          {showRatings && (
            <button
              onClick={loadPair}
              disabled={loading}
              className="button button-next-pair"
            >
              {loading ? 'Loading...' : 'Next Comparison'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}